import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import { isPrerendering } from '../../prerenderData';
import { useAuth } from '../../context/AuthContext';
import { customerAuthApi } from '../../services/customerAuthApi';
import './AccountPage.css';

function moneyAud(cents) {
  if (cents == null) return '-';
  return `$${(Number(cents) / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// UX-10: human-readable labels for the Order schema's status enum
// (server/src/models/Order.js — pending/paid/failed/refunded plus
// a 'new' value some legacy docs carry). Customers see these strings
// in their account, so they need to read as outcomes, not internal
// state.
const ORDER_STATUS_LABELS = {
  new: 'Processing',
  pending: 'Awaiting payment',
  paid: 'Confirmed',
  failed: 'Payment failed',
  refunded: 'Refunded',
};
function orderStatusLabel(status) {
  return ORDER_STATUS_LABELS[status] || status;
}

function AccountPage() {
  const { user, loading, logout, updateProfile } = useAuth();
  const [orders, setOrders] = useState(null);
  const [ordersError, setOrdersError] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!user) return;
    customerAuthApi.orders()
      .then(setOrders)
      .catch((err) => setOrdersError(err.message));
  }, [user]);

  useEffect(() => {
    if (user && !form) {
      setForm({
        full_name: user.full_name || '',
        phone: user.phone || '',
        address_line1: user.address_line1 || '',
        address_line2: user.address_line2 || '',
        suburb: user.suburb || '',
        state: user.state || 'VIC',
        postcode: user.postcode || '',
      });
    }
  }, [user, form]);

  // The pre-render captures the loading state, which is also what the
  // client renders first; anything later (the sign-in redirect, the account
  // itself) depends on the auth probe and cannot match the captured HTML.
  if (loading || isPrerendering) {
    return (
      <div className="acct-loading">
        <SEO title="My account" description="Your ASG Shading account - profile, delivery address and order history in one place." canonical="/account" noindex />
        <h1 className="acct-title">Your account</h1>
        <p>Loading your account…</p>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/sign-in?next=/account" replace />;
  }

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    try {
      await updateProfile(form);
      setEditing(false);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="acct-page">
      <SEO title="My account" description="Your ASG Shading account - profile, delivery address and order history in one place." canonical="/account" noindex />
      <div className="acct-inner">
        <header className="acct-header">
          <div>
            <p className="acct-eyebrow">My account</p>
            <h1 className="acct-title">Hi, {user.full_name.split(' ')[0]}</h1>
            <p className="acct-sub">{user.email}{user.email_verified ? '' : ' · email not verified yet'}</p>
          </div>
          <button type="button" className="acct-logout" onClick={logout}>Sign out</button>
        </header>

        <section className="acct-section">
          <div className="acct-section-head">
            <h2>Profile & address</h2>
            {!editing && <button type="button" className="acct-link" onClick={() => setEditing(true)}>Edit</button>}
          </div>
          {editing && form ? (
            <form className="acct-form" onSubmit={handleSaveProfile}>
              <div className="acct-row acct-row--2">
                <label>Full name<input type="text" value={form.full_name} onChange={update('full_name')} required /></label>
                <label>Phone<input type="tel" value={form.phone} onChange={update('phone')} /></label>
              </div>
              <label>Street address<input type="text" value={form.address_line1} onChange={update('address_line1')} /></label>
              <label>Apartment / unit<input type="text" value={form.address_line2} onChange={update('address_line2')} /></label>
              <div className="acct-row acct-row--3">
                <label>Suburb<input type="text" value={form.suburb} onChange={update('suburb')} /></label>
                <label>State<select value={form.state} onChange={update('state')}>
                  <option>VIC</option><option>NSW</option><option>QLD</option><option>WA</option>
                  <option>SA</option><option>TAS</option><option>ACT</option><option>NT</option>
                </select></label>
                <label>Postcode<input type="text" maxLength={4} value={form.postcode} onChange={update('postcode')} /></label>
              </div>
              {saveError && <p className="auth-error">{saveError}</p>}
              <div className="acct-form-actions">
                <button type="button" className="acct-link" onClick={() => setEditing(false)}>Cancel</button>
                <button type="submit" className="acct-btn" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          ) : (
            <div className="acct-profile-grid">
              <div><span>Name</span>{user.full_name}</div>
              <div><span>Phone</span>{user.phone || '-'}</div>
              <div className="acct-profile-grid--full"><span>Address</span>
                {(user.address_line1 || user.suburb || user.postcode)
                  ? `${user.address_line1}${user.address_line2 ? ', ' + user.address_line2 : ''}, ${user.suburb} ${user.state} ${user.postcode}`
                  : 'No saved address yet.'}
              </div>
            </div>
          )}
        </section>

        <section className="acct-section">
          <div className="acct-section-head">
            <h2>Your orders</h2>
            <Link to="/products" className="acct-link">Shop products →</Link>
          </div>
          {ordersError && <p className="auth-error">{ordersError}</p>}
          {orders == null && !ordersError && <p className="acct-empty">Loading…</p>}
          {orders && orders.length === 0 && (
            <p className="acct-empty">No orders yet. <Link to="/products">Browse the range</Link> to get started.</p>
          )}
          {orders && orders.length > 0 && (
            /* MOB-04: scroll wrapper so the table can overflow horizontally
               on phones instead of breaking the layout. The visible
               columns also collapse further at ≤480px (see AccountPage.css). */
            <div className="acct-orders-wrap">
              <table className="acct-orders">
                <thead>
                  <tr><th>Date</th><th>Product</th><th>Total</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id}>
                      <td>{new Date(o.created_at).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                      <td>{o.product_name}{o.variant_label ? ` - ${o.variant_label}` : ''}</td>
                      <td>{moneyAud(o.amount_cents)}</td>
                      <td><span className={`acct-status acct-status--${o.status}`}>{orderStatusLabel(o.status)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default AccountPage;
